import React, { useEffect, useMemo, useState } from 'react';

import { debounce } from 'lodash';

import {
  Box,
  Button,
  Container,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import PageHeader from '~/components/Common/Elements/PageHeader';

import { useGetFacebookContentImages } from '~/hooks/apis/public-offer-feed/useFacebookContentImages';

import dateFormatter from '~/utils/dateFormatter';

import ImageCarousel from './ImageCarousel';
import ImageViewerModal from './ImageViewerModal';
import RenderImageModal from './RenderImageModal';

interface OfferImageRow {
  id: string;
  offerId: string;
  imageCount: number;
  latestCreatedAt?: string;
  images: Array<{ id: string; imagePath: string; imageName?: string; templateName?: string; createdAt?: string }>;
}

export default function FacebookContentImagesContainer() {
  const [offerIdSearchInput, setOfferIdSearchInput] = useState('');
  const [offerIdSearch, setOfferIdSearch] = useState('');
  const [viewerOpen, setViewerOpen] = useState(false);
  const [selectedOfferId, setSelectedOfferId] = useState<string | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [renderModalOpen, setRenderModalOpen] = useState(false);
  const [pageNum, setPageNum] = useState(0);
  const pageSize = 20;

  // Debounce search input to avoid API calls on every keystroke
  const debouncedSetSearch = useMemo(
    () =>
      debounce((value: string) => {
        setOfferIdSearch(value);
        setPageNum(0); // Reset to first page when search changes
      }, 500),
    [],
  );

  useEffect(() => {
    debouncedSetSearch(offerIdSearchInput);
    return () => {
      debouncedSetSearch.cancel();
    };
  }, [offerIdSearchInput, debouncedSetSearch]);

  const { data, isLoading } = useGetFacebookContentImages(offerIdSearch ? { offerId: offerIdSearch } : undefined);

  const offerRows = useMemo(() => {
    if (!data) {
      return [];
    }

    // API response structure: { data: [{ offerId, images: [...] }], pagination: {...} }
    // Expected structure based on API response: { data: [{ offerId, images: [...] }] }
    const dataArray = data.data || [];

    // Convert to rows
    return dataArray.map((item) => {
      const offerId = item.offerId || 'unknown';
      const images = item.images || [];

      return {
        id: offerId,
        offerId,
        imageCount: images.length,
        latestCreatedAt: item.latestCreatedAt,
        images,
      } as OfferImageRow;
    });
  }, [data]);

  const handleThumbnailClick = (row: OfferImageRow, imageIndex: number) => {
    setSelectedOfferId(row.offerId);
    setSelectedImageIndex(imageIndex);
    setViewerOpen(true);
  };

  const handleCloseViewer = () => {
    setViewerOpen(false);
    setSelectedOfferId(null);
    setSelectedImageIndex(0);
  };

  const handleOpenRenderModal = () => {
    setRenderModalOpen(true);
  };

  const handleCloseRenderModal = () => {
    setRenderModalOpen(false);
  };

  const selectedOfferRow = offerRows.find((row) => row.offerId === selectedOfferId);
  const selectedOfferImages = selectedOfferRow?.images || [];

  // Pagination logic
  const paginatedRows = useMemo(() => {
    const startIndex = pageNum * pageSize;
    const endIndex = startIndex + pageSize;
    return offerRows.slice(startIndex, endIndex);
  }, [offerRows, pageNum, pageSize]);

  const totalPages = Math.ceil(offerRows.length / pageSize);

  const handlePageChange = (newPage: number) => {
    setPageNum(newPage);
  };

  return (
    <Container maxWidth="xl">
      <PageHeader title="Facebook Content Images">
        <Box>
          <Button variant="contained" color="primary" onClick={handleOpenRenderModal}>
            Trigger Render
          </Button>
        </Box>
      </PageHeader>

      <Box mt={4}>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            label="Search by Offer ID"
            value={offerIdSearchInput}
            onChange={(e) => setOfferIdSearchInput(e.target.value)}
            placeholder="Enter offer ID"
            size="small"
            sx={{ minWidth: 300 }}
          />
        </Stack>
      </Box>

      <Box mt={4}>
        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <Typography>Loading...</Typography>
          </Box>
        ) : offerRows.length === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <Typography variant="body1" color="text.secondary">
              No images found
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Offer ID</TableCell>
                  <TableCell>Number of Images</TableCell>
                  <TableCell>Latest Created At</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedRows.map((row) => (
                  <React.Fragment key={row.id}>
                    <TableRow>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {row.offerId}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {row.imageCount} {row.imageCount === 1 ? 'image' : 'images'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {row.latestCreatedAt ? dateFormatter(row.latestCreatedAt) : '—'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={3} sx={{ py: 2 }}>
                        <ImageCarousel
                          images={row.images}
                          onImageClick={(imageIndex) => handleThumbnailClick(row, imageIndex)}
                          maxThumbnails={5}
                        />
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {!isLoading && offerRows.length > 0 && (
          <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
            <Typography variant="body2" color="text.secondary">
              Showing {pageNum * pageSize + 1} to {Math.min((pageNum + 1) * pageSize, offerRows.length)} of{' '}
              {offerRows.length} offers
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => handlePageChange(pageNum - 1)}
                disabled={pageNum === 0}
              >
                Previous
              </Button>
              <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', px: 1 }}>
                Page {pageNum + 1} of {totalPages}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => handlePageChange(pageNum + 1)}
                disabled={pageNum >= totalPages - 1}
              >
                Next
              </Button>
            </Stack>
          </Box>
        )}
      </Box>

      <ImageViewerModal
        open={viewerOpen}
        onClose={handleCloseViewer}
        images={selectedOfferImages}
        currentIndex={selectedImageIndex}
        offerId={selectedOfferId || ''}
        onIndexChange={setSelectedImageIndex}
      />

      <RenderImageModal open={renderModalOpen} onClose={handleCloseRenderModal} />
    </Container>
  );
}
